import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useChat } from '../context/ChatContext'
import { TrashIcon, ChatBubbleLeftIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import ConfirmDialog from '../components/ConfirmDialog'
import LoadingSpinner from '../components/common/LoadingSpinner'
import toast from 'react-hot-toast'

const ConversationsPage = () => {
  const { conversations, loadConversations, deleteConversation, loading } = useChat()
  const [selectedConversations, setSelectedConversations] = useState([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [conversationToDelete, setConversationToDelete] = useState(null)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadConversations()
  }, [])

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleSelectConversation = (conversationId) => {
    setSelectedConversations(prev => {
      if (prev.includes(conversationId)) {
        return prev.filter(id => id !== conversationId)
      } else {
        return [...prev, conversationId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedConversations.length === filteredConversations.length) {
      setSelectedConversations([])
    } else {
      setSelectedConversations(filteredConversations.map(conv => conv._id))
    }
  }

  const handleDeleteSingle = (conversation) => {
    setConversationToDelete(conversation)
    setDeleteDialogOpen(true)
  }

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true)
  }

  const confirmDeleteSingle = async () => {
    if (conversationToDelete) {
      try {
        await deleteConversation(conversationToDelete._id)
        toast.success('Conversation deleted successfully')
      } catch (error) {
        toast.error('Failed to delete conversation')
      }
    }
  }

  const confirmBulkDelete = async () => {
    try {
      const deletePromises = selectedConversations.map(id => deleteConversation(id))
      await Promise.all(deletePromises)
      toast.success(`${selectedConversations.length} conversations deleted successfully`)
      setSelectedConversations([])
    } catch (error) {
      toast.error('Failed to delete some conversations')
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 1) return 'Today'
    if (diffDays === 2) return 'Yesterday'
    if (diffDays <= 7) return `${diffDays - 1} days ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">All Conversations</h1>
        <p className="text-gray-600">Manage and organize your chat history</p>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Select All */}
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
            >
              {selectedConversations.length === filteredConversations.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Bulk Actions */}
          {selectedConversations.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedConversations.length} selected
              </span>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 flex items-center"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Conversations List */}
      {filteredConversations.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No conversations found' : 'No conversations yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm 
              ? `No conversations match "${searchTerm}"`
              : 'Start chatting to see your conversations here'
            }
          </p>
          {!searchTerm && (
            <Link
              to="/chat"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              Start New Conversation
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((conversation) => (
            <div
              key={conversation._id}
              className={`bg-white rounded-lg shadow-sm border transition-all duration-200 ${
                selectedConversations.includes(conversation._id)
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedConversations.includes(conversation._id)}
                    onChange={() => handleSelectConversation(conversation._id)}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />

                  {/* Conversation Info */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/chat/${conversation._id}`}
                      className="block hover:text-primary-600 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {conversation.title}
                      </h3>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center">
                          <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                          {conversation.messages?.length || 0} messages
                        </span>
                        <span className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          {formatDate(conversation.lastActivity || conversation.createdAt)}
                        </span>
                      </div>
                    </Link>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/chat/${conversation._id}`}
                      className="px-3 py-1 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200"
                    >
                      Open
                    </Link>
                    <button
                      onClick={() => handleDeleteSingle(conversation)}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setConversationToDelete(null)
        }}
        onConfirm={confirmDeleteSingle}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${conversationToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Bulk Delete Dialog */}
      <ConfirmDialog
        isOpen={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Multiple Conversations"
        message={`Are you sure you want to delete ${selectedConversations.length} conversation${selectedConversations.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText={`Delete ${selectedConversations.length} Conversation${selectedConversations.length > 1 ? 's' : ''}`}
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ConversationsPage
